import { i18n } from '@/i18n';
import { readonly, ref } from 'vue';

export type GlobalLoadingOptions = {
  message?: string;
};

const visible = ref(false);
const message = ref('');

let activeCount = 0;
let currentMessage = '';

function resolveMessage(options?: string | GlobalLoadingOptions) {
  if (typeof options === 'string') {
    return options;
  }
  return options?.message;
}

function defaultMessage() {
  return i18n.global.t('common.globalLoading');
}

export function startGlobalLoading(options?: string | GlobalLoadingOptions) {
  const nextMessage = resolveMessage(options);
  activeCount += 1;

  if (nextMessage) {
    currentMessage = nextMessage;
    message.value = nextMessage;
  } else if (activeCount === 1) {
    message.value = currentMessage || defaultMessage();
  }

  visible.value = true;
}

export function stopGlobalLoading() {
  activeCount = Math.max(0, activeCount - 1);

  if (activeCount > 0) {
    return;
  }

  visible.value = false;
  message.value = '';
  currentMessage = '';
}

export async function withGlobalLoading<T>(
  task: () => Promise<T>,
  options?: string | GlobalLoadingOptions,
): Promise<T> {
  startGlobalLoading(options);
  try {
    return await task();
  } finally {
    stopGlobalLoading();
  }
}

export function useGlobalLoading() {
  return {
    visible: readonly(visible),
    message: readonly(message),
  };
}

export function resetGlobalLoadingForTests() {
  activeCount = 0;
  visible.value = false;
  message.value = '';
  currentMessage = '';
}
